import { useStore } from "@tanstack/react-form";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, PlusCircle } from "lucide-react";
import { useEffect, useState } from "react";
import {
  type CardRequestData,
  cardDefaults,
  createIndividualCardClient as cardValidator,
  refreshIntervalOptions,
} from "shared";
import { toast } from "sonner";
import type z from "zod";
import { AdvancedFields } from "@/components/fields/advanced-fields";
import {
  CardTypeField,
  cardTypeFieldMapping,
} from "@/components/fields/card-type-field";
import { LimitFlexibilityInputField } from "@/components/fields/limit-flexibility-input";
import {
  ShippingAddress,
  shippingAddressFieldMapping,
} from "@/components/fields/shipping-address";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { FormStepSeparator } from "@/components/ui/form-step-seperator";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAppForm } from "@/hooks/useCreateForm";
import { restrictRoute } from "@/lib/routeBlocker";

export const Route = createFileRoute("/_authed/_org/cards/create")({
  beforeLoad: async ({ context }) =>
    restrictRoute(context, {
      redirect: {
        to: "/cards",
      },
      requiredRole: "admin",
    }),
  component: RouteComponent,
});

function RouteComponent() {
  const { queryClient, trpc } = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const { mutate: issue, isPending: isIssuingCard } = useMutation(
    trpc.card.issue.mutationOptions({
      onError: (error) => {
        toast.error("Error", {
          description: error.message,
          position: "bottom-left",
        });
      },
      onSuccess: (newCard) => {
        navigate({
          params: {
            id: newCard.id,
          },
          to: "/cards/$id",
        });
        form.reset();

        queryClient.invalidateQueries(trpc.card.list.queryOptions());
        toast.success("Issued card", {
          position: "bottom-left",
        });
      },
    }),
  );

  const { mutate: scheduleCardForInvite, isPending: isSchedulingCard } =
    useMutation(
      trpc.card.scheduleInviteCard.mutationOptions({
        onError: (error) => {
          toast.error("Error", {
            description: error.message,
            position: "bottom-left",
          });
        },
        onSuccess: () => {
          form.reset();
          navigate({
            to: "/cards",
          });

          toast.success("Card scheduled successfully", {
            description:
              "Card will be issued once the user accepts their invite to join your organization on Givefront.",
            position: "bottom-left",
          });
        },
      }),
    );

  const form = useAppForm({
    canSubmitWhenInvalid: true,
    defaultValues: cardDefaults as z.infer<typeof cardValidator>,
    onSubmit: ({ value }) => {
      const { addressId: _addressId, address, ...rest } = value;

      const reqBody = {
        ...rest,
        // @ts-ignore
        address: value.type === "physical" ? address : undefined,
      };

      if (value.forUserId?.startsWith("inv_")) scheduleCardForInvite(reqBody);
      else issue(reqBody);
    },
    onSubmitInvalid: ({ formApi }) => {
      console.log("Form invalid", formApi.getAllErrors());
    },
    validators: {
      onSubmit: cardValidator,
    },
  });

  const { data: approvalPolicies } = useSuspenseQuery(
    trpc.approvalPolicy.list.queryOptions(),
  );

  const { data: requests } = useSuspenseQuery(trpc.request.list.queryOptions());
  const forUserId = useStore(form.store, (state) => state.values.forUserId);
  const cardType = useStore(form.store, (state) => state.values.type);
  const isValid = useStore(form.store, (state) => state.isValid);

  const pendingPhysicalRequest = requests.find(
    (request) =>
      request.type === "card" &&
      (request.data as CardRequestData).type === "physical" &&
      request.status === "pending" &&
      request.forUserId === forUserId,
  );
  const conflictingRequest = cardType === "physical" && pendingPhysicalRequest;

  const [alertOpen, setAlertOpen] = useState(false);

  useEffect(() => {
    if (!isValid) setAlertOpen(false);
  }, [isValid]);

  const isPending = forUserId?.startsWith("inv_")
    ? isSchedulingCard
    : isIssuingCard;

  return (
    <div className="flex h-full flex-col">
      <SheetHeader className="border-b px-6 py-6 pb-2">
        <SheetTitle>Issue a Card</SheetTitle>
      </SheetHeader>

      <form
        noValidate
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        {/* Scrollable content area with fixed max height */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-2 pb-4">
          <div className="mt-4 mb-2 space-y-4">
            <form.AppField
              children={(field) => (
                <field.UserSelectField
                  label="Card Holder"
                  required={true}
                  includePendingInvites
                />
              )}
              name="forUserId"
            />

            <form.Subscribe
              selector={(state) => state.values}
              children={(_state) => <div />}
            />

            <CardTypeField form={form} fields={cardTypeFieldMapping} />

            <FormStepSeparator description="Details" />

            <form.AppField
              children={(field) => (
                <field.TextField label="Card Nickname" required={true} />
              )}
              name="name"
            />

            <form.AppField
              children={(field) => {
                return (
                  <field.TextAreaField label="Description" required={false} />
                );
              }}
              name="description"
            />
          </div>

          <form.Subscribe
            selector={(state) => state.values.type}
            children={(type) =>
              type === "physical" && (
                <div className="my-4">
                  <FormStepSeparator description="Shipping" />

                  <ShippingAddress
                    form={form}
                    fields={shippingAddressFieldMapping}
                  />
                </div>
              )
            }
          />

          <FormStepSeparator description="Control Spend" />
          <div className="space-y-4">
            <LimitFlexibilityInputField
              form={form}
              fields={{
                restrictions: "restrictions",
              }}
            />
            <form.AppField
              // @ts-ignore Array types not being inferred correctly
              children={(field) => {
                return (
                  <field.SelectField
                    label="Frequency"
                    options={refreshIntervalOptions}
                    required={true}
                  />
                );
              }}
              name="restrictions.refreshInterval"
            />

            {approvalPolicies.length > 0 && (
              <FormStepSeparator description="Policies" />
            )}

            <div className="mb-4 flex flex-col gap-4">
              <form.AppField
                children={(field) => (
                  <field.ExpensePolicySelectField
                    label="Spend Policy"
                    mode="create"
                  />
                )}
                name="policyId"
              />

              {approvalPolicies.length > 0 && (
                <form.AppField
                  children={(field) => (
                    <field.ApprovalPolicySelectField
                      label="Approval Policy"
                      mode="create"
                    />
                  )}
                  name="approvalPolicyId"
                />
              )}
            </div>

            <div className="mt-6 mb-12 h-px w-full bg-border" />

            <AdvancedFields>
              <form.AppField
                // @ts-ignore Array types not being inferred correctly
                name="restrictions.allowedCategories"
                children={(field) => {
                  return (
                    <field.SpendCategorySelect
                      label="Allowed Spend Categories"
                      required={false}
                    />
                  );
                }}
              />
              <form.AppField
                name="restrictions.schedule.activateAt"
                children={(field) => (
                  <field.DateTimeField
                    label="Start Date"
                    descriptionStyle="below"
                    description="Card will be locked until this date"
                    range={{
                      before: new Date(),
                    }}
                  />
                )}
              />
              <form.Subscribe
                selector={(state) =>
                  state.values.restrictions.schedule?.activateAt
                }
                children={(activateAt) => (
                  <form.AppField
                    name="restrictions.schedule.lockAt"
                    children={(field) => (
                      <field.DateTimeField
                        label="Lock Date"
                        descriptionStyle="below"
                        description="Card will automatically lock on this date"
                        range={{
                          before: activateAt ?? new Date(),
                        }}
                      />
                    )}
                  />
                )}
              />

              <form.AppField
                name="defaultBudgetId"
                children={(field) => (
                  <field.BudgetSearchSelectField
                    label="Default Budget"
                    descriptionStyle="below"
                    description="Transactions on this card will be assigned to this budget by default"
                    buttonClassName="[&_*[data-placeholder=true]]:text-muted-foreground font-normal"
                    regressionToNullOption
                    required={false}
                  />
                )}
              />
            </AdvancedFields>
          </div>
        </div>

        <AlertDialog
          open={isValid && Boolean(conflictingRequest) && alertOpen}
          onOpenChange={setAlertOpen}
        >
          <div className="flex min-h-16 flex-row items-center justify-start border-t p-6">
            <AlertDialogTrigger
              asChild
              onClick={() => form.validateSync("submit")}
            >
              <Button
                className="font-semibold"
                disabled={isPending}
                size="default"
                type={conflictingRequest ? "button" : "submit"}
              >
                {isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <PlusCircle className="mr-2 h-4 w-4" />
                )}
                Create
              </Button>
            </AlertDialogTrigger>
          </div>

          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Conflicting physical card request
              </AlertDialogTitle>

              <AlertDialogDescription>
                Each member can only have one active physical card at a time.
                There is a pending request for a physical card for this member.
                If you issue this card, we'll automatically deny it for you.
              </AlertDialogDescription>

              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={form.handleSubmit}>
                  Issue anyway
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      </form>
    </div>
  );
}
